import { motion, AnimatePresence } from "motion/react";
import { CATEGORIES } from "@/types/video";
import type { RandomMode } from "@/types/video";

interface ModeSelectorProps {
  open: boolean;
  onClose: () => void;
  currentMode: RandomMode;
  currentCategory: string | null;
  onSelect: (mode: RandomMode, category: string | null) => void;
}

// Spec #14: channel-style mode picker (NOT a typical category page).

const MODES: { id: RandomMode; label: string; cn: string; glyph: string }[] = [
  { id: "random", label: "RANDOM", cn: "随机", glyph: "▣" },
  { id: "archive", label: "ARCHIVE", cn: "考古", glyph: "◴" },
  { id: "lost", label: "LOST", cn: "冷门宇宙", glyph: "◌" },
];

export default function ModeSelector({
  open,
  onClose,
  currentMode,
  currentCategory,
  onSelect,
}: ModeSelectorProps) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="absolute inset-0 bg-void/70 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            data-ui-zone
          />
          <motion.div
            className="absolute right-0 top-0 bottom-0 w-full sm:w-[520px] z-[100] glass-panel archive-panel p-6 sm:p-8 safe-top safe-bottom overflow-y-auto"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.28, ease: "easeOut" }}
            data-ui-zone
          >
            <div className="flex justify-between items-center mb-6">
              <div className="vfd text-xs tracking-widest2">CHANNEL</div>
              <button
                onClick={onClose}
                className="font-mono text-xs tracking-widest2 text-static hover:text-signal"
              >
                CLOSE [ESC]
              </button>
            </div>

            {/* Modes */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
              {MODES.map((m, i) => {
                const active = m.id === currentMode && !currentCategory;
                return (
                  <button
                    key={m.id}
                    onClick={() => onSelect(m.id, null)}
                    className={`text-left p-4 border rounded-xl transition-colors ${
                      active
                        ? "border-[#b93172] bg-[#fff0f7] text-[#8e1f56]"
                        : "border-[#d9a2bd] bg-white text-[#2c1836] hover:border-[#b93172]"
                    }`}
                  >
                    <div className="font-mono text-[10px] tracking-widest2 text-[#65476e]">
                      {String(i + 1).padStart(2, "0")} {m.glyph}
                    </div>
                    <div className="font-display font-medium text-lg mt-1 tracking-wide">
                      {m.label}
                    </div>
                    <div className="font-mono text-[10px] text-[#65476e] mt-1">
                      {m.cn}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Categories */}
            <div>
              <div className="font-mono text-[10px] tracking-widest2 text-static mb-3">
                BY CATEGORY
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {CATEGORIES.map((c, i) => {
                  const active = currentCategory === c.id;
                  return (
                    <button
                      key={c.id}
                      onClick={() => onSelect("archive", c.id)}
                      className={`text-center font-mono text-xs py-2 border tracking-wide transition-colors ${
                        active
                          ? "border-signal text-signal"
                          : "border-phosphor/10 hover:border-phosphor/40 text-static hover:text-phosphor"
                      }`}
                    >
                      <div className="text-[9px] text-dim">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div className="mt-0.5">{c.id}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 font-mono text-[10px] text-dim text-center tracking-widest2">
              [ R ] 切换频道 · 选好以后自动换台
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
